import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';

/*
 * /physiotherapists — rebuilt 2 Sep 2026 on the landing-page baseline.
 *
 * This was the last page still on pre-rebuild layout. It is the partner
 * surface: physiotherapists are supply, not an ICP, so this page recruits and
 * explains rather than markets.
 *
 * QA-07 HOLDS THE THREE SERVICE TYPES with CRO, with an explicit instruction to
 * leave them alone. The tier content below is carried across VERBATIM from the
 * previous version — names, definitions, who delivers, where, what is required,
 * and the gating notes. Only the layout around them changed. If CRO revises the
 * model, that section is replaced without touching the rest of the page.
 *
 * Access is by application, not self-serve: every CTA goes to /contact and we
 * provision. That closes the contradiction QA-06 left, where tier 1 was gated
 * in the copy while the button still opened a self-serve wizard.
 */

const PhysiotherapistsPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'Partner with us | PhysioActivity';
    }, []);

    const requestAccess = (source) => {
        firePAEvent('partner_cta_click', { source });
        navigate('/contact?who=physio');
    };

    return (
        <div className="pa2">

            <section className="hero" id="top">
                <div className="wrap two">
                    <div className="heromain">
                        <h1 className="on-dark">
                            <span className="setup">Care that does not stop</span>
                            <span className="payoff">at discharge.</span>
                        </h1>
                        <p className="lede">
                            Physiotherapy is episodic: a block of sessions, then the relationship
                            ends. Here you can see how someone is actually moving between
                            appointments, so there is something to work from and a reason to stay
                            in touch.
                        </p>
                        <p className="nerve">
                            Free for HCPC and GMC-registered clinicians. No software fees, and you
                            keep the client relationship.
                        </p>
                        <div className="cta-row">
                            <button className="btn btn-t" onClick={() => requestAccess('hero')}>
                                Request access
                            </button>
                        </div>
                    </div>

                    <div className="herovis">
                        <div className="dash" aria-label="CMPX practitioner view, illustrative">
                            <div className="d-top"><i /><i /><i /><span className="w">CMPX · practitioner view</span></div>
                            <div className="d-body">
                                <div className="d-side"><i className="on" /><i /><i /><i /><i /><i /></div>
                                <div className="d-main">
                                    <div className="d-h"><span className="t">Your clients</span><span className="s">Loading trends</span></div>
                                    <div className="d-tiles">
                                        <div><div className="v">34</div><div className="c">people in your care</div></div>
                                        <div><div className="v g">6</div><div className="c">above your threshold</div></div>
                                        <div><div className="v g">8 wks</div><div className="c">reporting window</div></div>
                                    </div>
                                    <div className="d-chart">
                                        <div className="cl">Adherence, last 8 weeks</div>
                                        <svg viewBox="0 0 320 74" width="100%" height="74" aria-hidden="true">
                                            <polyline points="0,58 45,52 90,55 135,44 180,38 225,30 270,26 320,20" fill="none" stroke="#4CB6C3" strokeWidth="3.4" strokeLinecap="round" />
                                            <polyline points="0,66 45,64 90,65 135,62 180,61 225,60 270,59 320,58" fill="none" stroke="#cbd6e0" strokeWidth="2.2" strokeDasharray="5 5" />
                                        </svg>
                                    </div>
                                    <div className="d-foot"><span>Thresholds you set</span><span>Export · CSV, PDF</span></div>
                                </div>
                            </div>
                        </div>
                        <p className="cap">Illustrative example. Not a real client list.</p>
                    </div>
                </div>
            </section>

            <section className="tint" id="why">
                <div className="wrap">
                    <h2>
                        <span className="setup">The platform underneath,</span>
                        <span className="payoff">not between you and them.</span>
                    </h2>
                    <p className="lede">
                        You are working from what actually happened rather than from what someone
                        remembers at their next appointment. We do not own the relationship, take
                        it over, or route your clients elsewhere.
                    </p>

                    <div className="cards">
                        <div className="card">
                            <h3>You keep the client</h3>
                            <p>The relationship is yours. We are the platform underneath it, not a party to it.</p>
                        </div>
                        <div className="card">
                            <h3>You set the thresholds</h3>
                            <p>Loading trends across everyone in your care, with the limits you choose rather than ones we chose.</p>
                        </div>
                        <div className="card">
                            <h3>You decide</h3>
                            <p>The software proposes adjustments. A named, HCPC-registered clinician decides, and that decision is recorded against you.</p>
                        </div>
                    </div>

                    <p className="quiet">
                        <Link to="/cmpx#roles">What CMPX does</Link> covers the practitioner view.{' '}
                        <Link to="/blog/who-writes-your-programme">Who writes your programme</Link>{' '}
                        sets out where the software stops and the clinician starts.
                    </p>
                </div>
            </section>

            {/* ==========================================================
                QA-07: HELD WITH CRO.

                The three service types, what each one is, who delivers it,
                where it happens and what it requires are all unchanged. The
                MODEL is untouched.

                The wording was condensed to bullets on 2 Sep, then rewritten
                for the right reader. The first pass carried "HCPC-registered
                or equivalent, vetted" as a bullet, which describes who is
                ELIGIBLE and tells a physiotherapist reading it nothing about
                the service. These bullets now describe what each service IS
                from the practitioner's side: what they publish, how it is
                delivered, what it asks of their diary, what it requires.

                Eligibility and vetting have not been dropped from the page.
                They are in the closing section and in the confirmation email,
                which is where they matter.

                CRO should see this, since it edits copy inside a held section
                even though it does not change what is being offered.
                ========================================================== */}
            <section className="white" id="services">
                <div className="wrap">
                    <h2><span className="setup">Three service types. Pick your mix.</span></h2>
                    <p className="lede">
                        Services are independent, not a ladder. Offer one, two, or all three,
                        whatever fits your setup today. Tell us which when you get in touch.
                    </p>

                    <div className="tiers">
                        <div className="tier">
                            <div className="k">Self-guided</div>
                            <h3>Design once, scale your care</h3>
                            <ul>
                                <li>Publish a programme once, delivered through the app</li>
                                <li>Patients follow it themselves, no live consult</li>
                                <li>Nothing booked against your diary</li>
                                <li>Needs a published programme and a profile page</li>
                            </ul>
                        </div>

                        <div className="tier">
                            <div className="k">Personalised</div>
                            <h3>Programme plus a monthly consult</h3>
                            <ul>
                                <li>Your programme, plus a monthly remote review</li>
                                <li>App between reviews, video for the consult</li>
                                <li>A retention tier: the relationship keeps going</li>
                                <li>Needs consult slots you can honour</li>
                            </ul>
                        </div>

                        <div className="tier">
                            <div className="k">Specialised</div>
                            <h3>In-person at a partner facility</h3>
                            <ul>
                                <li>Assessment in person, then care continues digitally</li>
                                <li>At a clinic, gait lab or performance centre</li>
                                <li>Referred bookings come to your facility</li>
                                <li>Needs a facility and the capacity to take them</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="s-promise" id="apply">
                <div className="wrap">
                    <h2 className="on-dark">
                        <span className="setup">Bring your own patients,</span>
                        <span className="payoff">and keep them.</span>
                    </h2>
                    <p className="lede">
                        Tell us about your practice and which service types interest you. We verify
                        HCPC registration before granting access, then set you up.
                    </p>
                    <div className="cta-row">
                        <button className="btn btn-t" onClick={() => requestAccess('closing')}>
                            Request access
                        </button>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default PhysiotherapistsPage;
